import {DialogContent, DialogTitle, TextField} from "@mui/material";
import {Dialog2} from "../../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../../misc/SmoothActionBar";
import {api} from "../../../../../api/API";
import {useAsync2} from "nate-react-api-helpers";
import {useProject, useProjectId} from "../../ProjectName";
import { useContext, useEffect, useState} from "react";
import {PDFInfo, PrintManagerContext} from "../../../../../misc/pdf/PrintManager";
import {KeyingPDF} from "./KeyingPDF";
import {useSubmittalPreferences} from "../../../../../misc/pdf/useSubmittalPreferences";
import {Project} from "../../../../../api/Projects";
import {Company} from "../../../../../api/Companies";
import {Contact} from "../../../../../api/Contacts";
import {useCurrentContractor} from "../../../quote/CurrentContractor";
import {ChangePreviewContext} from "../../ShopDrawingChangePreview";
import {Keying2Config, Keying2Label, Keying2Product} from "../../../../../api/Keying2";
import {KeyingGrouppedItem, KeyScheduleLine} from "./KeyingSchedule";
import {useReloader} from "../../hardware/ReloadContext";

type Keying2LabelExt = Keying2Label & {
    keyCountStr?: string
}

export function KeyingSubmittal(props: {
    onDone(): void;
}) {
    const pr = useProject();

    const printCtx = useContext(PrintManagerContext);

    const projectId = useProjectId();
    const ctx = useContext(ChangePreviewContext);
    const previewChangeset = ctx.enabled ? ctx.sessionId : undefined;

    const [labels, setLabels] = useState<Keying2LabelExt[]>([]);

    const keyingListReq = useAsync2(input => api.keying2.list(input), {project: projectId, previewChangeset}, [projectId, previewChangeset]);
    const keyingDetail = keyingListReq.result;
    const reload = useReloader();

    useEffect(() => {
        if(keyingDetail) {
            setLabels((keyingDetail.labelsAvailable || []).map(l => ({
                ...l,
                keyCountStr: l.keyCount.toString(),
            })));
        }
    }, [keyingDetail])

    const {getContractorDetails, CurrentContractorPromptElement} = useCurrentContractor({
        onAutoPromptCanceled: props.onDone
    });

    const prefs = useSubmittalPreferences({
        project: projectId,
        kind: "keying-schedule",
        onLoad: () => {},
        onLoadDeps: [],
    });

    if(CurrentContractorPromptElement) {
        return (<>{CurrentContractorPromptElement}</>);
    }

    if(!pr || keyingListReq.loading || prefs.loading) return null;
    const project = pr;

    const editLabel = (updated: Partial<Keying2LabelExt> & {oldName: string}) => {
        setLabels(old => old.map(l => (l.oldName || l.name) === updated.oldName ? Object.assign({}, l, updated) : l));
    }

    const submit = async () => {
        const labelsAvailable = labels.map(toSavedLabel);
        const labelsBefore = (keyingDetail?.labelsAvailable || []).map(toSavedLabel);

        // only save when the user edited a label, so printing alone doesn't open a changeset
        if(JSON.stringify(labelsAvailable) !== JSON.stringify(labelsBefore)) {
            await api.keying2.update(Object.assign({}, keyingDetail, {
                labelsAvailable,
            }));

            reload.current();
        }

        const {client, clientContact} = await getContractorDetails();

        await printCtx.print2(getKeyingPDF({
            project,
            client,
            clientContact,
            keyData: async () => {
                const keyData = await api.keying2.list({project: project.id});
                const keyOpenings = configToPerOpening(keyData)

                // sort by floor ascending, then by seqNumber
                keyOpenings.sort((a, b) => {
                    if(a.isMisc !== b.isMisc) {
                        return a.isMisc ? 1 : -1;
                    }

                    if(a.floor !== b.floor) {
                        const aFloor = parseInt(a.floor) || 0
                        const bFloor = parseInt(b.floor) || 0
                        return aFloor - bFloor
                    }

                    return a.seqNumber - b.seqNumber
                });

                return {
                    data: keyOpenings,
                    names: keyData.labelsAvailable || [],
                }
            },
            lastSubmission: prefs.lastSubmission,
        }));

        props.onDone();
    };

    return (
        <Dialog2 open maxWidth="md" fullWidth onClose={props.onDone} onEnterPress={submit}>
            <DialogTitle>Create Keying Submittal</DialogTitle>

            <DialogContent>
                <table>
                    <tbody>
                    <tr>
                        <th>Name</th>
                        <th>Title</th>
                        <th>Description</th>
                        <th># of Keys Included</th>
                    </tr>
                    {labels.map((n, index) => <tr key={index.toString()}>
                        <td style={{paddingRight: 20}}>
                            <TextField variant="outlined"
                                       size="small"
                                       value={n.name || ""}
                                       onChange={e => {
                                           editLabel({
                                               name: e.target.value,
                                               oldName: n.oldName || n.name,
                                           })
                                       }}
                            />
                        </td>
                        <td style={{paddingRight: 20}}>
                            <TextField variant="outlined"
                                       size="small"
                                       value={n.title || ""}
                                       onChange={e => {
                                           editLabel({
                                               title: e.target.value,
                                               oldName: n.oldName || n.name,
                                           })
                                       }}
                            />
                        </td>
                        <td style={{paddingRight: 20}}>
                            <TextField variant="outlined"
                                       size="small"
                                       value={n.description || ""}
                                       onChange={e => {
                                           editLabel({
                                               description: e.target.value,
                                               oldName: n.oldName || n.name,
                                           })
                                       }}
                            />
                        </td>
                        <td>
                            <TextField variant="outlined"
                                       size="small"
                                       // @ts-ignore
                                       value={n.keyCountStr || ""}
                                       onChange={e => {
                                           editLabel({
                                               keyCountStr: e.target.value,
                                               keyCount: parseInt(e.target.value || "0") || 0,
                                               oldName: n.oldName || n.name,
                                           })
                                       }}
                            />
                        </td>
                    </tr>)}
                    </tbody>
                </table>
            </DialogContent>
            <SmoothActionBar
                onCancel={props.onDone}
                onSubmit={submit}
                actionName="Create and Download"
            />
        </Dialog2>
    )
}

function toSavedLabel(k: Keying2Label): Keying2Label {
    return {
        name: k.name,
        oldName: k.oldName,
        title: k.title,
        keyCount: k.keyCount,
        description: k.description,
    };
}

export function configToPerOpening(k: Keying2Config): KeyScheduleLine[] {
    const out: KeyScheduleLine[] = [];
    const miscOut: KeyScheduleLine[] = [];

    k.shortcodes?.forEach(s => {

        s.openings?.forEach(o => {
            let validOpeningParts: string[] = ["active", "common"];
            if (o.openingType === "Pair") {
                validOpeningParts = ["active", "inactive", "common"];
            }

            const validProducts = (s.products || [] as Keying2Product[])
                .filter(a => (a.qtyMeta || []).filter(m => m.qty > 0).map(m => m.opening).includes(o.id))
                .map(p => p.product);

            const products = (s.products || [] as Keying2Product[])
                .filter(a => validProducts.includes(a.product))
                .filter(a => a.openingPart && validOpeningParts.includes(a.openingPart))
                .filter(a => a.labelsSelected.length > 0);

            const groupped = makeKeyingItemsGroupped(products, s.logisticsMode);

            out.push({
                ...o,
                isMisc: false,
                keyed: k.enabled && products.length > 0,
                grouppedItems: groupped,
            });
        });

        s.miscItems?.forEach(m => {
            const validProducts = (s.products || [] as Keying2Product[])
                .filter(a => (a.qtyMeta || []).filter(m => m.qty > 0).map(m => m.miscProduct).includes(m.id))
                .map(p => p.product);

            const products = (s.products || [] as Keying2Product[])
                .filter(a => validProducts.includes(a.product))
                .filter(a => a.labelsSelected.length > 0);

            const groupped = makeKeyingItemsGroupped(products, s.logisticsMode);

            miscOut.push({
                name: m.name,
                isMisc: true,
                keyed: k.enabled && m.qty > 0,
                floor: "",
                seqNumber: 0,
                locationTwo: "",
                grouppedItems: groupped,
            })
        });
    })

    return [...out, ...miscOut]
}

function calcBy(products: Keying2Product[], logisticsMode: "factory-key" | "on-site"): string {
    if (products.some(p => p.byOthers)) return "Others";
    if(products.length === 0) return "";

    switch(logisticsMode) {
        case "factory-key": return "Factory"
        case "on-site": return "On-Site";
        default: return logisticsMode
    }
}

function makeKeyingItemsGroupped(products: Keying2Product[], logisticsMode: "factory-key" | "on-site") {
    const groupped: KeyingGrouppedItem[] = [];

    products.forEach(p => {
        const by = calcBy([p], logisticsMode);
        const key = JSON.stringify({"part": p.openingPart, "labels": p.labelsSelected.sort(), "by": by});
        let obj = groupped.find(g => g.key === key);
        if (!obj) {
            obj = {
                key: key,
                openingPart: p.openingPart || "",
                products: [],
                labelsSelected: p.labelsSelected || [],
                by: by,
            }

            groupped.push(obj);
        }

        obj.products.push(p.productCategory + " - " + p.productCode);
    })

    return groupped;
}

export function getKeyingPDF(props: {
    project: Project;
    client?: Company;
    clientContact?: Contact;
    keyData: () => Promise<{
        data: KeyScheduleLine[]
        names: Keying2Label[]
    }>;
    lastSubmission: string | null;
}): PDFInfo {
    return {
        name: `keying-schedule.pdf`,
        render: async onReady => {
            const data = await props.keyData()

            return <KeyingPDF
                project={props.project}
                lastSubmittal={props.lastSubmission}
                client={props.client}
                clientContact={props.clientContact}
                onReady={onReady}
                rows={data.data}
                names={data.names}
            />
        },
        serverInfo: {
            submissionForProject: props.project.id,
            submissionType: "keying-schedule",
        }
    }
}

export type KeyingPDFInfo = {
    version: 1;
    keyCount: KeyCount[]
};

export type KeyCount = {
    name: string;
    value: string;
}